import { useState, useEffect, useCallback } from "react";
import { createAdminApiClient, resolveImageUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, GripVertical, Youtube, Upload, ImagePlus } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ images, onImagesChange, getAdminApi }) => {
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [urlInput, setUrlInput] = useState("");

    const uploadFile = async (file) => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const { username, password } = JSON.parse(credStr);

        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/admin/upload-image`, {
            method: 'POST',
            headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
            body: formData
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'Upload failed');
        }
        return res.json();
    };

    const handleFiles = async (files) => {
        const validFiles = Array.from(files).filter(f =>
            ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type)
        );
        if (validFiles.length === 0) {
            toast.error("Only JPG, PNG, WEBP, GIF images are allowed");
            return;
        }

        setUploading(true);
        const newUrls = [];
        for (const file of validFiles) {
            try {
                const result = await uploadFile(file);
                newUrls.push(`${API_URL}${result.url}`);
            } catch (err) {
                toast.error(`Failed to upload ${file.name}: ${err.message}`);
            }
        }
        if (newUrls.length > 0) {
            onImagesChange([...images, ...newUrls]);
            toast.success(`${newUrls.length} image(s) uploaded`);
        }
        setUploading(false);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    }, [images]);

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);

    const addUrl = () => {
        if (!urlInput.trim()) return;
        onImagesChange([...images, urlInput.trim()]);
        setUrlInput("");
    };

    const removeImage = (index) => {
        onImagesChange(images.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-3">
            {/* Drop Zone */}
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !uploading && document.getElementById('image-file-input').click()}
                className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                    dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="image-drop-zone"
            >
                <input
                    id="image-file-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
                {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <ImagePlus className="h-8 w-8 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                            <span className="text-primary font-bold">Click to upload</span> or drag & drop images here
                        </p>
                        <p className="text-xs text-muted-foreground/60">JPG, PNG, WEBP, GIF (max 10MB)</p>
                    </div>
                )}
            </div>

            {/* URL input as alternative */}
            <div className="flex gap-2">
                <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                    placeholder="Or paste an image URL..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrl())}
                />
                <Button type="button" onClick={addUrl} className="rounded-none" variant="outline">
                    Add URL
                </Button>
            </div>

            {/* Image previews */}
            {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {images.map((img, index) => (
                        <div key={index} className="relative group cursor-pointer" onClick={() => {
                            if (index === 0) return;
                            const reordered = [...images];
                            const [moved] = reordered.splice(index, 1);
                            reordered.unshift(moved);
                            onImagesChange(reordered);
                        }}>
                            <img src={resolveImageUrl(img)} alt="" className={`w-20 h-20 object-cover bg-secondary ${index === 0 ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-muted-foreground"}`} />
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X className="h-3 w-3" />
                            </button>
                            {index === 0 ? (
                                <span className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[9px] text-center py-0.5 font-mono">
                                    MAIN
                                </span>
                            ) : (
                                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5 font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                                    SET AS MAIN
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    
    const emptyProduct = {
        internal_name: "",
        display_title: "",
        short_description: "",
        long_description: "",
        primary_use_cases: [],
        best_for: [],
        not_designed_for: [],
        technical_specs: [],
        manufacturer: "",
        sales_service_provider: "Dry Ice Bulls",
        price_text: "",
        images: [],
        video_embed: "",
        upgrade_options: [],
        category_ids: [],
        is_featured: false,
        is_active: true,
        order: 0,
        availability_status: "in_stock",
        shipping_type: "heavy_machinery",
        shipping_cost: 350,
        shipping_label: "Insured Nationwide Flat-Rate Freight Shipping",
        monthly_payment_text: "",
        related_product_ids: [],
        stock_quantity: null
    };
    
    const [formData, setFormData] = useState(emptyProduct);
    
    // Temp inputs for array fields
    const [tempInputs, setTempInputs] = useState({
        use_case: "",
        best_for: "",
        not_for: "",
        image_url: "",
        spec_name: "",
        spec_value: "",
        spec_unit: "",
        upgrade_name: "",
        upgrade_desc: "",
        upgrade_price: ""
    });
    
    const getAdminApi = () => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const credentials = JSON.parse(credStr);
        return createAdminApiClient(credentials.username, credentials.password);
    };
    
    const fetchData = async () => {
        try {
            const adminApi = getAdminApi();
            if (!adminApi) {
                setLoading(false);
                return;
            }
            const [productsRes, categoriesRes] = await Promise.all([
                adminApi.getProducts(),
                adminApi.getCategories()
            ]);
            setProducts(productsRes.data);
            setCategories(categoriesRes.data);
        } catch (err) {
            console.error("Failed to fetch data:", err);
            toast.error("Failed to load products");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            ...product,
            primary_use_cases: product.primary_use_cases || [],
            best_for: product.best_for || [],
            not_designed_for: product.not_designed_for || [],
            technical_specs: product.technical_specs || [],
            images: product.images || [],
            upgrade_options: product.upgrade_options || [],
            category_ids: product.category_ids || [],
            related_product_ids: product.related_product_ids || []
        });
        setIsDialogOpen(true);
    };
    
    const handleCreate = () => {
        setEditingProduct(null);
        setFormData(emptyProduct);
        setIsDialogOpen(true);
    };
    
    const handleSave = async () => {
        try {
            const adminApi = getAdminApi();
            if (editingProduct) {
                await adminApi.updateProduct(editingProduct.id, formData);
                toast.success("Product updated successfully");
            } else {
                await adminApi.createProduct(formData);
                toast.success("Product created successfully");
            }
            setIsDialogOpen(false);
            fetchData();
        } catch (err) {
            console.error("Failed to save product:", err);
            toast.error("Failed to save product");
        }
    };
    
    const handleDelete = async (productId) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        
        try {
            const adminApi = getAdminApi();
            await adminApi.deleteProduct(productId);
            toast.success("Product deleted");
            fetchData();
        } catch (err) {
            console.error("Failed to delete product:", err);
            toast.error("Failed to delete product");
        }
    };
    
    // Array field helpers
    const addToArray = (field, value) => {
        if (!value.trim()) return;
        setFormData(prev => ({
            ...prev,
            [field]: [...prev[field], value.trim()]
        }));
    };
    
    const removeFromArray = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };
    
    const addSpec = () => {
        if (!tempInputs.spec_name || !tempInputs.spec_value) return;
        setFormData(prev => ({
            ...prev,
            technical_specs: [...prev.technical_specs, {
                name: tempInputs.spec_name,
                value: tempInputs.spec_value,
                unit: tempInputs.spec_unit
            }]
        }));
        setTempInputs(prev => ({ ...prev, spec_name: "", spec_value: "", spec_unit: "" }));
    };
    
    const removeSpec = (index) => {
        setFormData(prev => ({
            ...prev,
            technical_specs: prev.technical_specs.filter((_, i) => i !== index)
        }));
    };
    
    const addUpgrade = () => {
        if (!tempInputs.upgrade_name) return;
        setFormData(prev => ({
            ...prev,
            upgrade_options: [...prev.upgrade_options, {
                name: tempInputs.upgrade_name,
                description: tempInputs.upgrade_desc,
                price_text: tempInputs.upgrade_price
            }]
        }));
        setTempInputs(prev => ({ ...prev, upgrade_name: "", upgrade_desc: "", upgrade_price: "" }));
    };
    
    const removeUpgrade = (index) => {
        setFormData(prev => ({
            ...prev,
            upgrade_options: prev.upgrade_options.filter((_, i) => i !== index)
        }));
    };
    
    const toggleCategory = (categoryId) => {
        setFormData(prev => ({
            ...prev,
            category_ids: prev.category_ids.includes(categoryId)
                ? prev.category_ids.filter(id => id !== categoryId)
                : [...prev.category_ids, categoryId]
        }));
    };
    
    return (
        <div data-testid="admin-products">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Products</h1>
                    <p className="text-muted-foreground">Manage your equipment catalog</p>
                </div>
                <Button 
                    onClick={handleCreate}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                    data-testid="create-product-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Product
                </Button>
            </div>
            
            {/* Products Table */}
            <Card className="bg-card border border-border rounded-none">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : products.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            No products yet. Click "Add Product" to create your first product.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border bg-secondary/50">
                                    <TableHead className="font-mono uppercase text-xs">Order</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Product</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Internal Name</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Price</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Status</TableHead>
                                    <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {products.map((product) => (
                                    <TableRow key={product.id} className="border-border">
                                        <TableCell className="font-mono text-muted-foreground">
                                            <GripVertical className="h-4 w-4" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {product.images?.[0] ? (
                                                    <img src={resolveImageUrl(product.images[0])} alt="" className="w-12 h-12 object-cover bg-secondary" />
                                                ) : (
                                                    <div className="w-12 h-12 bg-secondary flex items-center justify-center text-xs text-muted-foreground">
                                                        No img
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-bold">{product.display_title}</p>
                                                    {product.is_featured && (
                                                        <Badge className="rounded-none bg-primary/10 text-primary border-0 text-xs mt-1">
                                                            Featured
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">{product.internal_name}</TableCell>
                                        <TableCell className="font-mono text-primary">{product.price_text}</TableCell>
                                        <TableCell>
                                            <Badge className={`rounded-none ${product.is_active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                                {product.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            
            {/* Edit/Create Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-card border-border rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl">
                            {editingProduct ? "Edit Product" : "Create Product"}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Internal Name *</Label>
                                <Input
                                    value={formData.internal_name}
                                    onChange={(e) => setFormData({ ...formData, internal_name: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="DIB-P410S"
                                    data-testid="product-internal-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Display Title *</Label>
                                <Input
                                    value={formData.display_title}
                                    onChange={(e) => setFormData({ ...formData, display_title: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="P410S Compact Blaster"
                                    data-testid="product-display-title"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Short Description *</Label>
                            <Textarea
                                value={formData.short_description}
                                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                rows={2}
                                data-testid="product-short-desc"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Long Description</Label>
                            <Textarea
                                value={formData.long_description || ""}
                                onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                rows={4}
                            />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Manufacturer</Label>
                                <Input
                                    value={formData.manufacturer}
                                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="Cold Jet"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Sales & Service By</Label>
                                <Input
                                    value={formData.sales_service_provider || ""}
                                    onChange={(e) => setFormData({ ...formData, sales_service_provider: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="Dry Ice Bulls"
                                    data-testid="product-service-provider"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Price Text *</Label>
                                <Input
                                    value={formData.price_text}
                                    onChange={(e) => setFormData({ ...formData, price_text: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="$15,990 or Contact for pricing"
                                    data-testid="product-price"
                                />
                            </div>
                        </div>
                        
                        {/* Stock Status, Quantity & Shipping */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Stock Status</Label>
                                <select
                                    value={formData.availability_status || "in_stock"}
                                    onChange={(e) => setFormData({ ...formData, availability_status: e.target.value })}
                                    className="w-full rounded-none bg-input border-transparent focus:border-primary h-10 px-3 text-sm"
                                    data-testid="product-availability"
                                >
                                    <option value="in_stock">In Stock - Ready to Ship</option>
                                    <option value="available_to_order">Available to Order - Ships in 10 Days</option>
                                    <option value="factory_order">Factory Order - 14-21 Days</option>
                                    <option value="out_of_stock">Out of Stock</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Stock Quantity</Label>
                                <Input
                                    type="number"
                                    value={formData.stock_quantity ?? ""}
                                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value === "" ? null : parseInt(e.target.value) })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="Leave empty for unlimited"
                                    data-testid="product-stock-quantity"
                                />
                                <p className="text-xs text-muted-foreground">Empty = unlimited stock. Auto-decrements on order.</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Shipping Type</Label>
                                <select
                                    value={formData.shipping_type || "heavy_machinery"}
                                    onChange={(e) => {
                                        const type = e.target.value;
                                        const defaults = type === "accessories"
                                            ? { shipping_cost: 15, shipping_label: "Standard Ground Shipping" }
                                            : { shipping_cost: 350, shipping_label: "Insured Nationwide Flat-Rate Freight Shipping" };
                                        setFormData({ ...formData, shipping_type: type, ...defaults });
                                    }}
                                    className="w-full rounded-none bg-input border-transparent focus:border-primary h-10 px-3 text-sm"
                                    data-testid="product-shipping-type"
                                >
                                    <option value="heavy_machinery">Heavy Machinery</option>
                                    <option value="accessories">Accessories/Nozzles</option>
                                </select>
                            </div>
                        </div>
                        
                        {/* Shipping Cost & Label */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Shipping Cost ($)</Label>
                                <Input
                                    type="number"
                                    value={formData.shipping_cost ?? 350}
                                    onChange={(e) => setFormData({ ...formData, shipping_cost: parseFloat(e.target.value) || 0 })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="350"
                                    data-testid="product-shipping-cost"
                                />
                            </div>
                            <div className="col-span-2 space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Shipping Label</Label>
                                <Input
                                    value={formData.shipping_label || ""}
                                    onChange={(e) => setFormData({ ...formData, shipping_label: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    placeholder="e.g. Insured Nationwide Flat-Rate Freight Shipping"
                                    data-testid="product-shipping-label"
                                />
                            </div>
                        </div>
                        
                        {/* Monthly Payment */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Monthly Financing Estimate (optional)</Label>
                            <Input
                                value={formData.monthly_payment_text || ""}
                                onChange={(e) => setFormData({ ...formData, monthly_payment_text: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                placeholder="e.g. $340.42/month"
                                data-testid="product-monthly-payment"
                            />
                            <p className="text-muted-foreground text-[10px]">Shown on product page under financing option. Leave blank to auto-calculate (price/48).</p>
                        </div>
                        
                        {/* Related Products */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Related Products ("This Could Also Interest You")</Label>
                            <div className="flex flex-wrap gap-2">
                                {products.filter(p => p.id !== editingProduct?.id).map((p) => (
                                    <Badge
                                        key={p.id}
                                        className={`cursor-pointer rounded-none ${
                                            formData.related_product_ids?.includes(p.id) 
                                                ? "bg-primary text-primary-foreground" 
                                                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                                        }`}
                                        onClick={() => {
                                            const ids = formData.related_product_ids || [];
                                            setFormData({
                                                ...formData,
                                                related_product_ids: ids.includes(p.id) 
                                                    ? ids.filter(id => id !== p.id) 
                                                    : [...ids, p.id]
                                            });
                                        }}
                                        data-testid={`related-toggle-${p.id}`}
                                    >
                                        {p.display_title}
                                    </Badge>
                                ))}
                            </div>
                            <p className="text-muted-foreground text-[10px]">Click to toggle. Selected products show as recommendations on this product's page.</p>
                        </div>
                        
                        {/* Categories */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Categories</Label>
                            <div className="flex flex-wrap gap-2">
                                {categories.map((cat) => (
                                    <Badge
                                        key={cat.id}
                                        className={`cursor-pointer rounded-none ${
                                            formData.category_ids.includes(cat.id)
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-secondary text-secondary-foreground'
                                        }`}
                                        onClick={() => toggleCategory(cat.id)}
                                    >
                                        {cat.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        
                        {/* Images - Drag & Drop Upload */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Images</Label>
                            <ImageUploader
                                images={formData.images}
                                onImagesChange={(images) => setFormData({ ...formData, images })}
                                getAdminApi={getAdminApi}
                            />
                        </div>
                        
                        {/* Video */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider flex items-center gap-2">
                                <Youtube className="h-4 w-4 text-red-500" />
                                YouTube Video URL
                            </Label>
                            <Input
                                value={formData.video_embed}
                                onChange={(e) => setFormData({ ...formData, video_embed: e.target.value })}
                                className="rounded-none bg-input border-transparent focus:border-primary"
                                placeholder="https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID"
                            />
                            <p className="text-xs text-muted-foreground">
                                Paste any YouTube URL - it will be automatically converted to embed format
                            </p>
                            {formData.video_embed && (
                                <div className="mt-2 p-3 bg-secondary/50 border border-border">
                                    <p className="text-xs text-muted-foreground mb-2">Video Preview:</p>
                                    <div className="aspect-video max-w-md bg-black">
                                        <iframe 
                                            src={(() => {
                                                const url = formData.video_embed;
                                                if (!url) return '';
                                                if (url.includes('/embed/')) return url;
                                                const watchMatch = url.match(/[?&]v=([^&]+)/);
                                                if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
                                                const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
                                                if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
                                                return url;
                                            })()}
                                            className="w-full h-full"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            title="Video Preview"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Technical Specs */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Technical Specs</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={tempInputs.spec_name}
                                    onChange={(e) => setTempInputs({ ...tempInputs, spec_name: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    placeholder="Name (e.g., Air Consumption)"
                                />
                                <Input
                                    value={tempInputs.spec_value}
                                    onChange={(e) => setTempInputs({ ...tempInputs, spec_value: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary w-24"
                                    placeholder="Value"
                                />
                                <Input
                                    value={tempInputs.spec_unit}
                                    onChange={(e) => setTempInputs({ ...tempInputs, spec_unit: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary w-20"
                                    placeholder="Unit"
                                />
                                <Button type="button" onClick={addSpec} className="rounded-none">Add</Button>
                            </div>
                            <div className="space-y-1 mt-2">
                                {formData.technical_specs.map((spec, index) => (
                                    <div key={index} className="flex items-center justify-between bg-secondary/50 p-2">
                                        <span>{spec.name}: {spec.value} {spec.unit}</span>
                                        <button type="button" onClick={() => removeSpec(index)} className="text-destructive">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Use Cases */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Primary Use Cases</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={tempInputs.use_case}
                                    onChange={(e) => setTempInputs({ ...tempInputs, use_case: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    placeholder="e.g., Automotive detailing"
                                />
                                <Button type="button" onClick={() => { addToArray('primary_use_cases', tempInputs.use_case); setTempInputs({ ...tempInputs, use_case: "" }); }} className="rounded-none">
                                    Add
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                                {formData.primary_use_cases.map((item, index) => (
                                    <Badge key={index} className="rounded-none bg-secondary text-secondary-foreground gap-1">
                                        {item}
                                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeFromArray('primary_use_cases', index)} />
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        
                        {/* Best For / Not For */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Best For</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={tempInputs.best_for}
                                        onChange={(e) => setTempInputs({ ...tempInputs, best_for: e.target.value })}
                                        className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    />
                                    <Button type="button" onClick={() => { addToArray('best_for', tempInputs.best_for); setTempInputs({ ...tempInputs, best_for: "" }); }} className="rounded-none">
                                        Add
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {formData.best_for.map((item, index) => (
                                        <Badge key={index} className="rounded-none bg-green-500/10 text-green-500 gap-1">
                                            {item}
                                            <X className="h-3 w-3 cursor-pointer" onClick={() => removeFromArray('best_for', index)} />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono text-xs uppercase tracking-wider">Not Designed For</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={tempInputs.not_for}
                                        onChange={(e) => setTempInputs({ ...tempInputs, not_for: e.target.value })}
                                        className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    />
                                    <Button type="button" onClick={() => { addToArray('not_designed_for', tempInputs.not_for); setTempInputs({ ...tempInputs, not_for: "" }); }} className="rounded-none">
                                        Add
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {formData.not_designed_for.map((item, index) => (
                                        <Badge key={index} className="rounded-none bg-red-500/10 text-red-500 gap-1">
                                            {item}
                                            <X className="h-3 w-3 cursor-pointer" onClick={() => removeFromArray('not_designed_for', index)} />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Upgrade Options */}
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Upgrade Options</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={tempInputs.upgrade_name}
                                    onChange={(e) => setTempInputs({ ...tempInputs, upgrade_name: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    placeholder="Name"
                                />
                                <Input
                                    value={tempInputs.upgrade_desc}
                                    onChange={(e) => setTempInputs({ ...tempInputs, upgrade_desc: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary flex-1"
                                    placeholder="Description"
                                />
                                <Input
                                    value={tempInputs.upgrade_price}
                                    onChange={(e) => setTempInputs({ ...tempInputs, upgrade_price: e.target.value })}
                                    className="rounded-none bg-input border-transparent focus:border-primary w-28"
                                    placeholder="Price"
                                />
                                <Button type="button" onClick={addUpgrade} className="rounded-none">Add</Button>
                            </div>
                            <div className="space-y-1 mt-2">
                                {formData.upgrade_options.map((upgrade, index) => (
                                    <div key={index} className="flex items-center justify-between bg-secondary/50 p-2">
                                        <span>{upgrade.name} {upgrade.price_text && `(${upgrade.price_text})`}</span>
                                        <button type="button" onClick={() => removeUpgrade(index)} className="text-destructive">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Switches */}
                        <div className="flex items-center gap-8">
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={formData.is_featured}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                                />
                                <Label>Featured Product</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                />
                                <Label>Active (Visible on Site)</Label>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-wider">Display Order</Label>
                            <Input
                                type="number"
                                value={formData.order}
                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                className="rounded-none bg-input border-transparent focus:border-primary w-24"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" className="rounded-none">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none" data-testid="save-product-btn">
                            {editingProduct ? "Save Changes" : "Create Product"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminProducts;
